/**
 * IndexedDB wrapper for storing audio blobs.
 *
 * Blobs are stored separately from queue metadata because:
 * 1. They're too large for localStorage (~480KB per 30s chunk)
 * 2. IndexedDB handles binary data natively (no base64 encoding)
 * 3. IndexedDB has much larger storage limits (quota-based)
 */
import posthog from "posthog-js";

import { logTimestamp } from "./index";

const LOG_PREFIX = () => `[${logTimestamp()}][TranscriptionQueue:IDB]`;

const DB_NAME = "scribe-transcription-queue";
const DB_VERSION = 1;
const BLOB_STORE = "blobs";

/** Blobs and queue items older than this are removed automatically (3 days). */
export const TRANSCRIPTION_QUEUE_BLOB_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * Aggressive cutoff used only when storage is under pressure.
 * Keeps the default retention at 3 days but falls back to 24h if we're close to quota.
 */
export const TRANSCRIPTION_QUEUE_AGGRESSIVE_PRUNE_AGE_MS = 24 * 60 * 60 * 1000;

/** Fraction of the storage quota at which we trigger aggressive pruning. */
export const STORAGE_PRESSURE_THRESHOLD = 0.9;

/** Max reconnection attempts before giving up */
const MAX_RECONNECT_ATTEMPTS = 3;
/** Base delay for exponential backoff (ms) */
const RECONNECT_BASE_DELAY_MS = 500;

interface StoredBlob {
  key: string;
  /** Audio data as ArrayBuffer to avoid Blob structured clone failures in IndexedDB */
  data: ArrayBuffer;
  /** MIME type to reconstruct the Blob on retrieval */
  mimeType: string;
  sessionId: string;
  createdAt: number;
}

/** Pre-fix format that stored raw Blobs directly */
interface LegacyStoredBlob {
  key: string;
  blob: Blob;
  sessionId: string;
  createdAt: number;
}

let dbInstance: IDBDatabase | null = null;
let dbPromise: Promise<IDBDatabase> | null = null;

function invalidateConnection() {
  dbInstance = null;
  dbPromise = null;
}

/**
 * Opens (or creates) the IndexedDB database.
 * Returns a cached instance if already open.
 * If the connection was closed unexpectedly, retries with exponential backoff.
 */
function openDB(): Promise<IDBDatabase> {
  if (dbInstance) {
    return Promise.resolve(dbInstance);
  }

  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = (async () => {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt < MAX_RECONNECT_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        const delay = RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1);
        console.log(
          `${LOG_PREFIX()} Retrying IndexedDB open (attempt ${attempt + 1}/${MAX_RECONNECT_ATTEMPTS}, delay ${delay}ms)`,
        );
        await new Promise((r) => setTimeout(r, delay));
      }

      try {
        const db = await new Promise<IDBDatabase>((resolve, reject) => {
          const request = indexedDB.open(DB_NAME, DB_VERSION);

          request.onerror = () => {
            reject(
              new Error(`Failed to open IndexedDB: ${request.error?.message}`),
            );
          };

          request.onsuccess = () => {
            const db = request.result;

            db.onclose = () => {
              console.warn(
                `${LOG_PREFIX()} IndexedDB connection closed unexpectedly`,
              );
              posthog.capture("transcription_indexeddb_connection_closed");
              invalidateConnection();
            };

            resolve(db);
          };

          request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;

            if (!db.objectStoreNames.contains(BLOB_STORE)) {
              const store = db.createObjectStore(BLOB_STORE, {
                keyPath: "key",
              });
              store.createIndex("by-session", "sessionId", { unique: false });
              store.createIndex("by-created", "createdAt", { unique: false });
            }
          };
        });

        dbInstance = db;
        return db;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        console.error(
          `${LOG_PREFIX()} IndexedDB open attempt ${attempt + 1} failed:`,
          err,
        );
      }
    }

    invalidateConnection();
    posthog.capture("transcription_indexeddb_reconnect_failed", {
      attempts: MAX_RECONNECT_ATTEMPTS,
    });
    throw lastError;
  })();

  return dbPromise;
}

/**
 * Returns the current `navigator.storage.estimate()` result, or null if the API
 * is unavailable or throws.
 */
export async function getStorageEstimate(): Promise<{
  usage: number;
  quota: number;
  ratio: number;
} | null> {
  if (typeof navigator === "undefined" || !navigator.storage?.estimate) {
    return null;
  }
  try {
    const { usage, quota } = await navigator.storage.estimate();
    // usage === 0 is valid (empty origin); guard against missing values
    // and quota === 0 to avoid divide-by-zero.
    if (usage === undefined || !quota) return null;
    return { usage, quota, ratio: usage / quota };
  } catch {
    return null;
  }
}

/**
 * Asks the browser to mark this origin's storage as persistent so it won't be
 * evicted under storage pressure. Safe to call repeatedly; returns whether
 * persistence is granted.
 */
const PERSISTENT_STORAGE_EVENT_KEY =
  "transcription-queue:persistent-storage-event-captured";

export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.storage?.persist) {
    return false;
  }
  try {
    if (await navigator.storage.persisted()) {
      return true;
    }
    const granted = await navigator.storage.persist();
    console.log(
      `${LOG_PREFIX()} Persistent storage ${granted ? "granted" : "denied"}`,
    );
    // Capture at most once per browser session so denied/unsupported origins
    // don't flood PostHog on every app init.
    const alreadyCaptured =
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(PERSISTENT_STORAGE_EVENT_KEY) !== null;
    if (!alreadyCaptured) {
      posthog.capture("transcription_persistent_storage_result", { granted });
      try {
        sessionStorage?.setItem(PERSISTENT_STORAGE_EVENT_KEY, "1");
      } catch {
        // sessionStorage may be unavailable (e.g. private mode quota); ignore.
      }
    }
    return granted;
  } catch (err) {
    console.warn(`${LOG_PREFIX()} requestPersistentStorage failed:`, err);
    return false;
  }
}

/**
 * Listener invoked with the set of blob keys removed during aggressive pruning
 * so callers (e.g., the transcription queue store) can drop the corresponding
 * metadata and avoid zombie items that reference missing blobs.
 */
type AggressivePruneListener = (deletedKeys: string[]) => void;
let aggressivePruneListener: AggressivePruneListener | null = null;

export function setAggressivePruneListener(
  listener: AggressivePruneListener | null,
): void {
  aggressivePruneListener = listener;
}

/**
 * If the origin is near its storage quota, aggressively prune blobs older than
 * the 24h cutoff (rather than the default 3-day retention). Returns the number
 * of blobs deleted, or 0 if no pruning occurred.
 */
async function pruneIfUnderStoragePressure(): Promise<number> {
  const estimate = await getStorageEstimate();
  if (!estimate || estimate.ratio < STORAGE_PRESSURE_THRESHOLD) {
    return 0;
  }
  console.warn(
    `${LOG_PREFIX()} Storage pressure detected (${(estimate.ratio * 100).toFixed(1)}% used) — pruning blobs older than 24h`,
  );
  try {
    const deletedKeys = await deleteOldBlobs(
      TRANSCRIPTION_QUEUE_AGGRESSIVE_PRUNE_AGE_MS,
    );
    if (deletedKeys.length > 0 && aggressivePruneListener) {
      try {
        aggressivePruneListener(deletedKeys);
      } catch (listenerErr) {
        console.error(
          `${LOG_PREFIX()} Aggressive prune listener threw:`,
          listenerErr,
        );
      }
    }
    posthog.capture("transcription_indexeddb_pressure_prune", {
      usage: estimate.usage,
      quota: estimate.quota,
      ratio: estimate.ratio,
      deleted: deletedKeys.length,
    });
    return deletedKeys.length;
  } catch (err) {
    console.error(`${LOG_PREFIX()} Pressure prune failed:`, err);
    return 0;
  }
}

/**
 * Stores a blob in IndexedDB.
 */
export async function storeBlob(
  key: string,
  blob: Blob,
  sessionId: string,
): Promise<void> {
  console.log(
    `${LOG_PREFIX()} Storing blob: key=${key}, size=${blob.size} bytes, session=${sessionId}`,
  );

  await pruneIfUnderStoragePressure();

  // Convert Blob → ArrayBuffer before storing. Storing raw Blobs in IndexedDB
  // can fail with "Error preparing Blob/File data to be stored in object store"
  // when the Blob's underlying data is detached or the browser can't serialize it.
  // ArrayBuffer is always safely cloneable by IndexedDB's structured clone algorithm.
  const arrayBuffer = await blob.arrayBuffer();
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(BLOB_STORE, "readwrite");
    const store = transaction.objectStore(BLOB_STORE);

    const data: StoredBlob = {
      key,
      data: arrayBuffer,
      mimeType: blob.type,
      sessionId,
      createdAt: Date.now(),
    };

    const request = store.put(data);

    request.onerror = () => {
      console.error(
        `${LOG_PREFIX()} Failed to store blob: ${request.error?.message}`,
      );
      reject(new Error(`Failed to store blob: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      console.log(`${LOG_PREFIX()} Blob stored successfully: key=${key}`);
      resolve();
    };
  });
}

/**
 * Retrieves a blob from IndexedDB.
 * Returns undefined if not found.
 */
export async function getBlob(key: string): Promise<Blob | undefined> {
  console.log(`${LOG_PREFIX()} Retrieving blob: key=${key}`);
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(BLOB_STORE, "readonly");
    const store = transaction.objectStore(BLOB_STORE);
    const request = store.get(key);

    request.onerror = () => {
      console.error(
        `${LOG_PREFIX()} Failed to get blob: ${request.error?.message}`,
      );
      reject(new Error(`Failed to get blob: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      const data = request.result as StoredBlob | LegacyStoredBlob | undefined;
      if (data && "data" in data && data.data) {
        // Current format: ArrayBuffer + mimeType
        const blob = new Blob([data.data], { type: data.mimeType });
        console.log(
          `${LOG_PREFIX()} Blob retrieved: key=${key}, size=${blob.size} bytes`,
        );
        resolve(blob);
      } else if (data && "blob" in data && data.blob) {
        // Legacy format: raw Blob stored before the ArrayBuffer fix
        console.log(
          `${LOG_PREFIX()} Blob retrieved (legacy format): key=${key}, size=${data.blob.size} bytes`,
        );
        resolve(data.blob);
      } else {
        console.warn(`${LOG_PREFIX()} Blob not found: key=${key}`);
        resolve(undefined);
      }
    };
  });
}

/**
 * Deletes a blob from IndexedDB.
 */
export async function deleteBlob(key: string): Promise<void> {
  console.log(`${LOG_PREFIX()} Deleting blob: key=${key}`);
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(BLOB_STORE, "readwrite");
    const store = transaction.objectStore(BLOB_STORE);
    const request = store.delete(key);

    request.onerror = () => {
      console.error(
        `${LOG_PREFIX()} Failed to delete blob: ${request.error?.message}`,
      );
      reject(new Error(`Failed to delete blob: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      console.log(`${LOG_PREFIX()} Blob deleted: key=${key}`);
      resolve();
    };
  });
}

/**
 * Deletes all blobs for a session.
 */
export async function deleteSessionBlobs(sessionId: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(BLOB_STORE, "readwrite");
    const store = transaction.objectStore(BLOB_STORE);
    const index = store.index("by-session");
    const request = index.openCursor(IDBKeyRange.only(sessionId));

    request.onerror = () => {
      reject(
        new Error(`Failed to delete session blobs: ${request.error?.message}`),
      );
    };

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
  });
}

/**
 * Gets all blob keys in the database.
 * Used for orphan cleanup during recovery.
 */
export async function getAllBlobKeys(): Promise<string[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(BLOB_STORE, "readonly");
    const store = transaction.objectStore(BLOB_STORE);
    const request = store.getAllKeys();

    request.onerror = () => {
      reject(new Error(`Failed to get blob keys: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      resolve(request.result as string[]);
    };
  });
}

/**
 * Deletes blobs older than the specified age and returns the keys that were
 * removed so callers can reconcile any external metadata they maintain.
 */
export async function deleteOldBlobs(maxAgeMs: number): Promise<string[]> {
  const db = await openDB();
  const cutoff = Date.now() - maxAgeMs;
  const deletedKeys: string[] = [];

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(BLOB_STORE, "readwrite");
    const store = transaction.objectStore(BLOB_STORE);
    const index = store.index("by-created");
    const request = index.openCursor(IDBKeyRange.upperBound(cutoff));

    request.onerror = () => {
      reject(
        new Error(`Failed to delete old blobs: ${request.error?.message}`),
      );
    };

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        deletedKeys.push(String(cursor.primaryKey));
        cursor.delete();
        cursor.continue();
      } else {
        resolve(deletedKeys);
      }
    };
  });
}

/**
 * Checks if IndexedDB is available in this environment.
 */
export function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch {
    return false;
  }
}
